import {
  Color3,
  MeshBuilder,
  Quaternion,
  Scene,
  StandardMaterial,
  TransformNode,
  Vector3,
} from '@babylonjs/core';
import { RAPIER, type PhysicsWorld } from '@roadworld/physics';
import type { IVehicle } from '../vehicle/IVehicle.js';
import type { ChunkManager } from './ChunkManager.js';
import {
  TREE_CROWN_BASE_DIAMETER,
  TREE_CROWN_HEIGHT,
  TREE_TOTAL_HEIGHT,
  TREE_TRUNK_DIAMETER,
  TREE_TRUNK_HEIGHT,
} from './trees.js';

/** Per-chunk tree-collider bookkeeping. Each chunk gets one shared fixed body that owns
 *  individual cylinder colliders for nearby trees; colliders are created and destroyed
 *  lazily based on the vehicle's distance (`cullRadius`). */
interface ChunkTreePhysics {
  body: RAPIER.RigidBody;
  /** Parallel to chunk.getTreeBatch().trees — collider, or null if not provisioned. */
  colliders: (RAPIER.Collider | null)[];
}

/** A tree mid-fall. The pivot lives at the tree's base; rotating it around `axis` topples
 *  the mesh in the direction the car was going. After `elapsed >= total` the tree just
 *  sits there as a static log until its chunk unloads. */
interface FallingTree {
  chunkKey: string;
  pivot: TransformNode;
  /** World-horizontal axis the tree rotates around as it falls (⟂ to fall direction). */
  axis: Vector3;
  /** Original standing yaw — we re-compose each frame so the trunk keeps its rotation. */
  yawQuat: Quaternion;
  elapsed: number;
  total: number;
  /** Tree size factor — passed through to the land SFX so bigger trees thud deeper. */
  scale: number;
  /** Latches true the frame `elapsed` first crosses `total`. Used to fire onTreeLand once. */
  landed: boolean;
}

/** Fires once per felled tree, the frame the fall is triggered by a car impact. */
export interface TreeHitEvent {
  worldX: number;
  worldZ: number;
  /** Pre-step car speed at impact, km/h. */
  speedKmh: number;
  /** Tree size factor (1.0 = default). */
  scale: number;
}

/** Fires once per tree, the frame its fall animation completes. */
export interface TreeLandEvent {
  worldX: number;
  worldZ: number;
  scale: number;
}

export class TreeImpactSystem {
  private scene: Scene;
  private physics: PhysicsWorld;
  private chunks: ChunkManager;
  private vehicle: IVehicle;
  private chunkState = new Map<string, ChunkTreePhysics>();
  /** colliderHandle → which tree it represents. Lets contact callbacks resolve identity. */
  private colliderIdentity = new Map<number, { chunkKey: string; idx: number }>();
  private falling: FallingTree[] = [];
  private fallingIdCounter = 0;
  private trunkMat: StandardMaterial | null = null;
  private crownMat: StandardMaterial | null = null;
  /** Fired AFTER a tree is felled locally — net layer rebroadcasts this to other players. */
  onLocalFell: ((ev: { chunkKey: string; idx: number; dirX: number; dirZ: number }) => void) | null = null;
  /** Subscriber sets — listeners notified each time a tree is hit/lands.
   *  Set-of-callbacks pattern matches `physics.onContact`. */
  private hitListeners = new Set<(e: TreeHitEvent) => void>();
  private landListeners = new Set<(e: TreeLandEvent) => void>();

  constructor(scene: Scene, physics: PhysicsWorld, chunks: ChunkManager, vehicle: IVehicle) {
    this.scene = scene;
    this.physics = physics;
    this.chunks = chunks;
    this.vehicle = vehicle;
  }

  /** Re-point the tree system at the currently-active vehicle (after a swap). Both
   *  vehicles share the same IVehicle surface so the tree system doesn't care which
   *  concrete class is backing it — main.ts just hands the new one over. */
  setVehicle(vehicle: IVehicle): void {
    this.vehicle = vehicle;
  }

  /**
   * Apply a tree-fell event received from another player. Hides the tree, destroys its
   * collider (if any), and spawns the same falling visual — no effect on the local vehicle.
   */
  applyRemoteFell(chunkKey: string, idx: number, dirX: number, dirZ: number): void {
    const chunk = this.chunks.getChunkByKey(chunkKey);
    if (!chunk) return;
    const batch = chunk.getTreeBatch();
    if (!batch) return;
    if (idx < 0 || idx >= batch.trees.length) return;
    if (batch.isHidden(idx)) return;
    const tree = batch.trees[idx]!;
    const wx = batch.worldX[idx]!;
    const wz = batch.worldZ[idx]!;
    batch.hide(idx);
    const state = this.chunkState.get(chunkKey);
    if (state) this.destroyCollider(state, idx);
    this.spawnFallingTree(chunkKey, tree.scale, tree.y, tree.yaw, wx, wz, dirX, dirZ);
  }

  /** Call once per frame, after physics.step. */
  update(dt: number): void {
    this.syncChunkLifecycle();
    this.provisionColliders();
    this.detectAndFell();
    this.advanceAnimations(dt);
  }

  /** Subscribe to per-tree hit events. Returns an unsubscribe function. */
  onTreeHit(cb: (e: TreeHitEvent) => void): () => void {
    this.hitListeners.add(cb);
    return () => this.hitListeners.delete(cb);
  }

  /** Subscribe to per-tree land events (fall animation completes). Returns an unsubscribe function. */
  onTreeLand(cb: (e: TreeLandEvent) => void): () => void {
    this.landListeners.add(cb);
    return () => this.landListeners.delete(cb);
  }

  /** Remove physics + falling-tree state for chunks that have been disposed. */
  private syncChunkLifecycle(): void {
    for (const [key, state] of this.chunkState) {
      if (!this.chunks.getChunkByKey(key)) {
        // Removing the body cleans up all attached colliders in one go.
        this.physics.world.removeRigidBody(state.body);
        for (const c of state.colliders) {
          if (c) this.colliderIdentity.delete(c.handle);
        }
        this.chunkState.delete(key);
      }
    }
    if (this.falling.length === 0) return;
    const kept: FallingTree[] = [];
    for (const f of this.falling) {
      if (this.chunks.getChunkByKey(f.chunkKey)) {
        kept.push(f);
      } else {
        f.pivot.dispose();
      }
    }
    this.falling = kept;
  }

  /** For each loaded chunk: bbox-cull against the player, then per-tree create/destroy
   *  colliders so only those near the player are active. */
  private provisionColliders(): void {
    const player = this.vehicle.position();
    const cull = this.vehicle.effectiveTreeFelling().cullRadius;
    const cullSq = cull * cull;

    for (const chunk of this.chunks.activeChunks()) {
      if (!chunk.isActive()) continue;
      const batch = chunk.getTreeBatch();
      if (!batch) continue;

      let state = this.chunkState.get(chunk.key());
      if (!state) {
        const body = this.physics.world.createRigidBody(RAPIER.RigidBodyDesc.fixed());
        state = { body, colliders: new Array(batch.trees.length).fill(null) };
        this.chunkState.set(chunk.key(), state);
      }

      const half = chunk.chunkSize * 0.5;
      const cx = chunk.coord.cx * chunk.chunkSize;
      const cz = chunk.coord.cz * chunk.chunkSize;
      const clampedX = clamp(player.x, cx - half, cx + half);
      const clampedZ = clamp(player.z, cz - half, cz + half);
      const edgeDx = player.x - clampedX;
      const edgeDz = player.z - clampedZ;
      const edgeDistSq = edgeDx * edgeDx + edgeDz * edgeDz;

      if (edgeDistSq > cullSq) {
        // Whole chunk out of range — drop any colliders we'd allocated.
        this.dropAllColliders(state);
        continue;
      }

      for (let i = 0; i < batch.trees.length; i++) {
        if (batch.isHidden(i)) {
          if (state.colliders[i]) this.destroyCollider(state, i);
          continue;
        }
        const wx = batch.worldX[i]!;
        const wz = batch.worldZ[i]!;
        const dx = player.x - wx;
        const dz = player.z - wz;
        const distSq = dx * dx + dz * dz;
        const has = state.colliders[i] !== null;
        if (distSq <= cullSq && !has) {
          this.createCollider(state, chunk.key(), i, batch.trees[i]!.scale, batch.trees[i]!.y, wx, wz);
        } else if (distSq > cullSq && has) {
          this.destroyCollider(state, i);
        }
      }
    }
  }

  private createCollider(
    state: ChunkTreePhysics,
    chunkKey: string,
    idx: number,
    scale: number,
    baseY: number,
    wx: number,
    wz: number,
  ): void {
    const cfg = this.vehicle.effectiveTreeFelling();
    const halfHeight = TREE_TOTAL_HEIGHT * 0.5 * scale;
    const radius = cfg.colliderRadius * scale;
    const desc = RAPIER.ColliderDesc.cylinder(halfHeight, radius)
      .setTranslation(wx, baseY + halfHeight, wz)
      .setFriction(0.6);
    const collider = this.physics.world.createCollider(desc, state.body);
    state.colliders[idx] = collider;
    this.colliderIdentity.set(collider.handle, { chunkKey, idx });
  }

  private destroyCollider(state: ChunkTreePhysics, idx: number): void {
    const collider = state.colliders[idx];
    if (!collider) return;
    this.colliderIdentity.delete(collider.handle);
    this.physics.world.removeCollider(collider, true);
    state.colliders[idx] = null;
  }

  private dropAllColliders(state: ChunkTreePhysics): void {
    for (let i = 0; i < state.colliders.length; i++) {
      if (state.colliders[i]) this.destroyCollider(state, i);
    }
  }

  /** Look at which tree colliders the vehicle is currently touching; for any of them, if
   *  the car was fast enough ENTERING the physics step, fell the tree and restore velocity.
   *  Otherwise Rapier's solid-collider response handles the stop on its own.
   *
   *  Key subtlety: we use the *pre-step* speed (captured by vehicle.beginFrame() before
   *  physics.step), not the current post-step speed. A solid tree collider can fully zero
   *  the car's velocity in a single 8 ms substep, so reading current speed here would see
   *  ~0 and incorrectly decide "too slow to fell" for a 70 km/h hit. After felling, we
   *  overwrite the body's velocity with the expected post-fell value so Rapier's stopping
   *  impulse — applied to a tree that's now gone — gets undone. */
  private detectAndFell(): void {
    const cfg = this.vehicle.effectiveTreeFelling();
    const preStepKmh = this.vehicle.preStepHSpeedMs() * 3.6;
    if (preStepKmh < cfg.fellMinSpeedKmh) return;

    const vehicleCollider = this.vehicle.getChassisCollider();

    // Snapshot the hits before mutating; contactPairsWith fires synchronously.
    const hits: Array<{ chunkKey: string; idx: number }> = [];
    this.physics.world.contactPairsWith(vehicleCollider, (other) => {
      const id = this.colliderIdentity.get(other.handle);
      if (id) hits.push(id);
    });

    if (hits.length === 0) return;

    // Track the speed we expect the car to be at after each fell, stacking losses. Restore
    // to this in the pre-step direction at the end if we felled anything.
    let expectedKmh = preStepKmh;
    let felledCount = 0;

    for (const hit of hits) {
      // If a prior fell this tick dropped expected speed below threshold, the remaining
      // trees should stop us (not fell). Leaves their colliders intact for next frame.
      if (expectedKmh < cfg.fellMinSpeedKmh) break;

      const state = this.chunkState.get(hit.chunkKey);
      if (!state) continue;
      const chunk = this.chunks.getChunkByKey(hit.chunkKey);
      if (!chunk) continue;
      const batch = chunk.getTreeBatch();
      if (!batch || batch.isHidden(hit.idx)) continue;
      const tree = batch.trees[hit.idx]!;
      const wx = batch.worldX[hit.idx]!;
      const wz = batch.worldZ[hit.idx]!;
      // Fall direction = vehicle → tree, horizontally. Geometric "I hit it from this side"
      // is more reliable than vehicle velocity: holds up under glancing hits, side
      // collisions, and slides where velocity is not parallel to the approach line.
      const carPos = this.vehicle.position();
      let dirX = wx - carPos.x;
      let dirZ = wz - carPos.z;
      const len = Math.hypot(dirX, dirZ);
      if (len > 1e-3) {
        dirX /= len;
        dirZ /= len;
      } else {
        // Degenerate (car center exactly on tree base): pick any direction.
        dirX = 0;
        dirZ = 1;
      }
      batch.hide(hit.idx);
      this.destroyCollider(state, hit.idx);
      this.spawnFallingTree(hit.chunkKey, tree.scale, tree.y, tree.yaw, wx, wz, dirX, dirZ);
      if (this.onLocalFell) {
        this.onLocalFell({ chunkKey: hit.chunkKey, idx: hit.idx, dirX, dirZ });
      }
      // Notify SFX/UI listeners — fires once per tree felled this frame. Uses the
      // running `expectedKmh` so each subsequent tree's hit reflects the accumulated
      // post-loss speed rather than the pristine pre-step value.
      if (this.hitListeners.size > 0) {
        const ev: TreeHitEvent = { worldX: wx, worldZ: wz, speedKmh: expectedKmh, scale: tree.scale };
        for (const cb of this.hitListeners) cb(ev);
      }
      // Piecewise-linear speed penalty: full at threshold, lerping to 0 at "zero" point,
      // 0 above. Applied to the running `expectedKmh` so multiple trees in one tick stack.
      const span = cfg.fellLossZeroAtKmh - cfg.fellMinSpeedKmh;
      const t = span > 0 ? (expectedKmh - cfg.fellMinSpeedKmh) / span : 1;
      const tClamped = t < 0 ? 0 : t > 1 ? 1 : t;
      const lossKmh = cfg.fellLossAtMinKmh * (1 - tClamped);
      expectedKmh -= lossKmh;
      if (expectedKmh < 0) expectedKmh = 0;
      felledCount++;
    }

    // Restore velocity to the expected post-fell value in the pre-step direction. Skipped
    // when nothing felled (avoid touching the body when Rapier's response was the desired
    // outcome, e.g. a sub-threshold hit on a tree).
    if (felledCount > 0) {
      const dir = this.vehicle.preStepHDirection();
      this.vehicle.setHorizontalVelocityMs(dir.x, dir.z, expectedKmh / 3.6);
    }
  }

  private spawnFallingTree(
    chunkKey: string,
    scale: number,
    baseY: number,
    yaw: number,
    wx: number,
    wz: number,
    dirX: number,
    dirZ: number,
  ): void {
    // Rotation axis is horizontal, perpendicular to fall direction. The sign here is
    // chosen so that a positive rotation tilts the top of the tree toward (dirX, 0, dirZ)
    // in Babylon's left-handed scene — i.e. the tree falls AWAY from the car. Flipping
    // these signs reverses the fall direction.
    const axis = new Vector3(dirZ, 0, -dirX);

    if (!this.trunkMat) {
      this.trunkMat = new StandardMaterial('falling-trunk-mat', this.scene);
      this.trunkMat.diffuseColor = new Color3(0.32, 0.22, 0.16);
      this.trunkMat.specularColor = new Color3(0.02, 0.02, 0.02);
    }
    if (!this.crownMat) {
      this.crownMat = new StandardMaterial('falling-crown-mat', this.scene);
      this.crownMat.diffuseColor = new Color3(0.18, 0.42, 0.2);
      this.crownMat.specularColor = new Color3(0.02, 0.02, 0.02);
    }

    const id = this.fallingIdCounter++;
    const pivot = new TransformNode(`falling-tree-${id}`, this.scene);
    pivot.position.set(wx, baseY, wz);
    const yawQuat = Quaternion.RotationAxis(Vector3.UpReadOnly, yaw);
    pivot.rotationQuaternion = yawQuat.clone();

    const trunk = MeshBuilder.CreateCylinder(
      `falling-trunk-${id}`,
      { diameter: TREE_TRUNK_DIAMETER, height: TREE_TRUNK_HEIGHT, tessellation: 6 },
      this.scene,
    );
    trunk.material = this.trunkMat;
    trunk.parent = pivot;
    trunk.position.set(0, TREE_TRUNK_HEIGHT * 0.5 * scale, 0);
    trunk.scaling.set(scale, scale, scale);

    const crown = MeshBuilder.CreateCylinder(
      `falling-crown-${id}`,
      {
        diameterTop: 0,
        diameterBottom: TREE_CROWN_BASE_DIAMETER,
        height: TREE_CROWN_HEIGHT,
        tessellation: 8,
      },
      this.scene,
    );
    crown.material = this.crownMat;
    crown.parent = pivot;
    crown.position.set(0, (TREE_TRUNK_HEIGHT + TREE_CROWN_HEIGHT * 0.5) * scale, 0);
    crown.scaling.set(scale, scale, scale);

    this.falling.push({
      chunkKey,
      pivot,
      axis,
      yawQuat,
      elapsed: 0,
      total: this.vehicle.effectiveTreeFelling().fallSeconds,
      scale,
      landed: false,
    });
  }

  private advanceAnimations(dt: number): void {
    if (this.falling.length === 0) return;
    for (const f of this.falling) {
      if (f.landed) continue; // already fully fallen and notified
      f.elapsed = Math.min(f.elapsed + dt, f.total);
      const t = f.elapsed / f.total;
      // Ease-in: tree starts slow then accelerates as it falls — feels more "physical".
      const eased = t * t;
      const angle = eased * Math.PI * 0.5;
      // fall axis is world-horizontal; apply yaw first, then fall, so the trunk keeps
      // its original spin while toppling away from the car.
      const fallQ = Quaternion.RotationAxis(f.axis, angle);
      f.pivot.rotationQuaternion = fallQ.multiply(f.yawQuat);

      // Land transition: fire onTreeLand exactly once on the completion frame,
      // synced with the visual coming to rest at PI/2.
      if (f.elapsed >= f.total) {
        f.landed = true;
        if (this.landListeners.size > 0) {
          const ev: TreeLandEvent = {
            worldX: f.pivot.position.x,
            worldZ: f.pivot.position.z,
            scale: f.scale,
          };
          for (const cb of this.landListeners) cb(ev);
        }
      }
    }
  }

  /** Drop every per-chunk physics body, every falling-tree visual, and every collider
   *  identity. Call this whenever the world frame changes (loading a new location); chunk
   *  keys like "0,0" are reused across sessions, so without an explicit reset the system
   *  would happily reuse the previous session's collider table for the new chunks, leaving
   *  the new trees uncollidable. Safe to call after `chunks.reset()` — the per-chunk Chunk
   *  bodies are already gone by then; this method only owns the tree-specific bodies. */
  reset(): void {
    for (const f of this.falling) f.pivot.dispose();
    this.falling = [];
    for (const state of this.chunkState.values()) {
      this.physics.world.removeRigidBody(state.body);
    }
    this.chunkState.clear();
    this.colliderIdentity.clear();
  }

  dispose(): void {
    this.reset();
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
